import { UIEvent, useEffect, useRef } from 'react';
import useDebounce from '../../../../hooks/useDebounce/useDebounce';
import useOnKeyPressIfFocused from '../../../../hooks/useOnKeyPressIfFocused';
import { normalizeString, valueMatchWithQuery } from '../../../../utils/string';
import { Option } from '../components/OptionsList/optionsList.types';
import { UseDynamicOptionsSelectorProps } from './useMultiOptionsSelector.types';
import useMultiOptionsSelectorV2 from './useMultiOptionsSelectorV2';

const normalizeStringToCompare = (string: string) => normalizeString(string).replace(/[./-]/g, '');
export const compareOptionWithQuery = (option: Option, queryToFilter: string) => {
  const optionIsVisible = valueMatchWithQuery(
    option.label,
    queryToFilter,
    normalizeStringToCompare,
  );
  return optionIsVisible;
};

const useDynamicOptionsSelector = ({
  options = [],
  value,
  onChange,
  loadMoreOptions,
  isLoadingOptions,
}: UseDynamicOptionsSelectorProps) => {
  const { handleSelectMultiple, textFieldRef, onChangeQuery, query } = useMultiOptionsSelectorV2({
    onChange,
    value,
  });

  const handleClearSelectedOptions = () => {
    onChange([]);
  };

  const handleScroll = (e: UIEvent<HTMLElement>) => {
    if (!loadMoreOptions || isLoadingOptions) {
      return;
    }

    const list = e.currentTarget;
    const endReached = list.scrollHeight - list.scrollTop <= list.clientHeight + 40;
    if (endReached && !isLoadingOptions) {
      loadMoreOptions(query || undefined);
    }
  };

  const isLoadedDynamicOptionsRef = useRef(false);

  const handleLoadFirstDynamicOptions = () => {
    const isLoadedDynamicOptions = isLoadedDynamicOptionsRef.current;
    if (isLoadedDynamicOptions || !loadMoreOptions) {
      return;
    }
    loadMoreOptions();
    isLoadedDynamicOptionsRef.current = true;
  };

  useEffect(() => {
    const needLoadSpecificsOptionsBefore = value.length !== options.length;

    if (isLoadingOptions || needLoadSpecificsOptionsBefore) {
      return;
    }

    handleLoadFirstDynamicOptions();
  }, [isLoadingOptions]);

  const debouncedQuery = useDebounce(query, 1500);

  useEffect(() => {
    if (!debouncedQuery || !loadMoreOptions) {
      return;
    }

    loadMoreOptions(debouncedQuery);
  }, [debouncedQuery]);

  const onPressEnter = () => {
    const visibleOptions = options.filter((opt) => compareOptionWithQuery(opt, query));
    if (!visibleOptions.length) {
      return;
    }

    const firstVisibleOption = visibleOptions[0];
    handleSelectMultiple(firstVisibleOption.value);
  };

  useOnKeyPressIfFocused({
    eventKey: 'Enter',
    ref: textFieldRef,
    onPress: onPressEnter,
  });

  return {
    textFieldRef,
    query,
    setQuery: onChangeQuery,
    handleScroll,
    handleSelectMultiple,
    handleClearSelectedOptions,
  };
};

export default useDynamicOptionsSelector;
